/**
 * Spaces tab for the Ranking Evaluation structure.
 *
 * Lists conversation spaces available for evaluation with filtering by site and
 * selection controls. Selected spaces are stored in module session state and
 * consumed by the Side-by-Side and Comment Scoring tabs.
 */

import { isoToMs } from '@repo/shared/orpc/schemas';
import { Banner } from 'component/banner';
import { HorizBox, VertBox } from 'component/basics';
import { CTAButton, FilterButton, SubtleButton } from 'component/button';
import type { TableColumn, TableColumnWidgetProps } from 'component/dashboard/table-grid';
import { fromNow } from 'component/date';
import { Checkbox } from 'component/form/checkbox';
import type { Space, SpaceFilter } from 'component/spaces-table';
import { SpacesTable } from 'component/spaces-table';
import { UtilityText } from 'component/text';
import { useLanguage } from 'component/translation';
import type { ExternalEvalSpace } from 'data/ranking-eval';
import {
    refreshEvalConversationsAsync,
    updateSelectedEvalConversationSetAsync,
    useImportableEvalConversations,
    useSelectedEvalConversationSet,
} from 'data/ranking-eval';
import { useState } from 'react';
import { showApiErrorToast } from 'system/orpc-errors';
import { useDatastore, useSessionData } from 'util/datastore';
import { t } from 'util/stdtypes';

// ============================================================================
// TABLE COLUMNS
// ============================================================================

function NameWidget({ item }: TableColumnWidgetProps<Space>) {
    const datastore = useDatastore();
    const selectedSet = useSelectedEvalConversationSet();
    const selectedKeys = selectedSet?.conversationKeys || [];
    const isSelected = selectedKeys.includes(item.key);

    async function toggleSelection() {
        const currentKeys = selectedKeys;
        const currentNames = selectedSet?.conversationNames || {};
        const newKeys = isSelected ? currentKeys.filter((k) => k !== item.key) : [...currentKeys, item.key];
        const newNames = { ...currentNames, [item.key]: item.name };

        await updateSelectedEvalConversationSetAsync(datastore, { conversationKeys: newKeys, conversationNames: newNames });
    }

    return <Checkbox onChange={toggleSelection} text={item.name} value={isSelected} />;
}

export const NameColumn: TableColumn<Space> = {
    label: t('ranking-eval.spaces.column_name', 'Name'),
    widget: NameWidget,
    span: 3,
};

function CommentsWidget({ item }: TableColumnWidgetProps<Space>) {
    return <UtilityText text={(item.commentCount ?? 0).toString()} type='small' />;
}

export const CommentsColumn: TableColumn<Space> = {
    label: t('ranking-eval.spaces.column_comments', 'Comments'),
    widget: CommentsWidget,
};

function CreatedWidget({ item }: TableColumnWidgetProps<Space>) {
    const language = useLanguage();
    return <UtilityText text={fromNow(item.time, language) ?? 'N/A'} type='small' />;
}

export const CreatedColumn: TableColumn<Space> = {
    label: t('ranking-eval.spaces.column_created', 'Created'),
    widget: CreatedWidget,
};

function ActionsWidget({ item }: TableColumnWidgetProps<Space>) {
    const datastore = useDatastore();

    function handleSideBySide() {
        datastore.gotoInstanceScreen({
            structureKey: 'ranking-eval',
            instanceKey: 'one',
            screenKey: `side-by-side/${item.key}`,
        });
    }

    function handleScoreComments() {
        datastore.gotoInstanceScreen({
            structureKey: 'ranking-eval',
            instanceKey: 'one',
            screenKey: `comment-scoring/${item.key}`,
        });
    }

    return (
        <HorizBox gap={8} right>
            <SubtleButton label={t('ranking-eval.spaces.side_by_side', 'Side-by-Side')} onPress={handleSideBySide} />
            <SubtleButton label={t('ranking-eval.spaces.score_comments', 'Score Comments')} onPress={handleScoreComments} />
        </HorizBox>
    );
}

export const ActionsColumn: TableColumn<Space> = {
    label: t('ranking-eval.spaces.column_actions', 'Actions'),
    alignRight: true,
    widget: ActionsWidget,
};

// ============================================================================
// FILTERS AND HELPERS
// ============================================================================

export function EmptySpacesWidget() {
    return (
        <VertBox gap={8}>
            <UtilityText label={t('ranking-eval.no_spaces', 'No spaces yet. Add one above to get started.')} type='small' />
        </VertBox>
    );
}

function conversationToSpace(conversation: ExternalEvalSpace): Space {
    return {
        key: conversation.key,
        name: conversation.name,
        structureKey: conversation.structureKey,
        commentCount: conversation.commentCount,
        time: conversation.createdAt ? isoToMs(conversation.createdAt) : undefined,
        ...(conversation.sourceKey && { sourceKey: conversation.sourceKey }),
    } as Space;
}

const KNOWN_SITES = [
    { key: 'np', label: 'New Public', baseUrl: 'https://psi.newpublic.org', siloKey: 'np' },
    { key: 'cbc', label: 'CBC', baseUrl: 'https://psi.newpublic.org', siloKey: 'cbc' },
    { key: 'rc', label: 'Radio Canada', baseUrl: 'https://conversations.radio-canada.ca', siloKey: 'rc' },
    { key: 'rtbf', label: 'RTBF', baseUrl: 'https://psi.newpublic.org', siloKey: 'rtbf' },
    { key: 'zdf', label: 'ZDF', baseUrl: 'https://psi.zdf.de', siloKey: 'zdf' },
];

function SourceFilterWidget() {
    const datastore = useDatastore();
    const activeSource = useSessionData('spaces_source_filter') || 'all';

    function setActiveSource(source: string) {
        datastore.setSessionData('spaces_source_filter', source);
    }

    return (
        <HorizBox gap={8}>
            <FilterButton
                label={t('ranking-eval.filter.all_sources', 'All Sources')}
                onPress={() => setActiveSource('all')}
                selected={activeSource === 'all'}
            />
            {KNOWN_SITES.map((site) => (
                <FilterButton
                    key={site.key}
                    onPress={() => setActiveSource(site.key)}
                    selected={activeSource === site.key}
                    text={site.label}
                />
            ))}
        </HorizBox>
    );
}

export const SourceFilter: SpaceFilter = {
    key: 'source',
    widget: SourceFilterWidget,
    sessionKey: 'spaces_source_filter',
    filter: (sourceFilterSetting, item: Space) => {
        const activeSource = sourceFilterSetting || 'all';
        if (activeSource === 'all') return true;
        return (item as any).sourceKey === activeSource;
    },
};

// ============================================================================
// SPACES TAB COMPONENT
// ============================================================================

export function SpacesTab() {
    const datastore = useDatastore();
    const conversations = useImportableEvalConversations();
    const [refreshing, setRefreshing] = useState(false);

    async function handleRefresh() {
        setRefreshing(true);
        try {
            await refreshEvalConversationsAsync(datastore);
        } catch (error) {
            showApiErrorToast({ datastore, error });
        }
        setRefreshing(false);
    }

    const conversationList = Object.values(conversations);
    const spaces = conversationList.map(conversationToSpace).toSorted((a, b) => (b.time ?? 0) - (a.time ?? 0));
    const isEmpty = spaces.length === 0;

    return (
        <VertBox gap={16}>
            <HorizBox center gap={12}>
                <CTAButton
                    disabled={refreshing}
                    label={
                        refreshing
                            ? t('ranking-eval.refresh_in_progress', 'Refreshing...')
                            : t('ranking-eval.refresh', 'Refresh from sources')
                    }
                    onPress={handleRefresh}
                    type='secondary'
                />
                {refreshing && (
                    <UtilityText label={t('ranking-eval.refreshing', 'Fetching conversations from partner sites...')} type='small' />
                )}
            </HorizBox>
            {isEmpty && !refreshing ? (
                <Banner>
                    <UtilityText
                        label={t(
                            'ranking-eval.no_cached',
                            'No conversations cached yet. Press "Refresh from sources" to fetch conversations from partner sites.',
                        )}
                        type='small'
                    />
                </Banner>
            ) : (
                <SpacesTable itemList={spaces} />
            )}
        </VertBox>
    );
}
